import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Inactivity timeout in milliseconds (1 hour = 3600000 ms)
  const INACTIVITY_TIMEOUT = 3600000;

  let inactivityTimer;

  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    inactivityTimer = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT);
  };

  const handleLogout = (redirectTo = '/user-login') => {
    // Check which tokens exist and clear only relevant ones
    const hasUserToken = localStorage.getItem('userToken');
    const hasAdminToken = localStorage.getItem('adminToken');

    // If logging out from admin route, only clear admin token
    if (hasAdminToken && !hasUserToken) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRole');
      localStorage.removeItem('adminName');
    }
    // If logging out from user route, only clear user token
    else if (hasUserToken && !hasAdminToken) {
      localStorage.removeItem('userToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('name');
    }
    // If both exist (shouldn't happen but safety), clear both
    else {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRole');
      localStorage.removeItem('adminName');
      localStorage.removeItem('userToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('name');
    }

    setIsAuthenticated(false);
    navigate(redirectTo);
  };

  const handleActivity = () => {
    resetInactivityTimer();
  };

  // Set up inactivity tracking when authentication state changes
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const addEventListeners = () => {
      events.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });
    };

    const removeEventListeners = () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };

    if (isAuthenticated) {
      addEventListeners();
      resetInactivityTimer();
    } else {
      removeEventListeners();
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
    }

    return () => {
      removeEventListeners();
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
    };
  }, [isAuthenticated]);

  // Initial authentication check on mount
  useEffect(() => {
    const userToken = localStorage.getItem('userToken');
    const adminToken = localStorage.getItem('adminToken');
    setIsAuthenticated(!!(userToken || adminToken));
  }, []);

  // Update authentication state when localStorage changes
  useEffect(() => {
    const checkAuth = () => {
      const userToken = localStorage.getItem('userToken');
      const adminToken = localStorage.getItem('adminToken');
      setIsAuthenticated(!!(userToken || adminToken));
    };

    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const value = {
    isAuthenticated,
    handleLogout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
